import React, { useState } from 'react';

import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useSendAthleteAttendance from 'hooks/athleteEvent/useSendAthleteAttendance';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import { AthleteEvent } from 'types';
import notificationHandler from 'utils/notifications';

import AttendancePageHeader from './components/AttendancePageHeader';
import AttendanceTableContainer from './components/AttendanceTableContainer';

interface FilterTerms {
  timeRange: 'thirtyDays' | 'ninetyDays' | 'sixMonths' | 'year';
  type: 'attended' | 'all';
}

const today = new Date();
today.setHours(0);
today.setMinutes(0);
today.setSeconds(0);

const Attendance = (): JSX.Element => {
  const initialSearchFiltersValue = {
    timeRange: 'thirtyDays' as const,
    type: 'attended' as const,
  };
  const [searchFilters, setSearchFilters] = useState<FilterTerms>({ ...initialSearchFiltersValue });

  const setTimeRange = (timeRange: 'thirtyDays' | 'ninetyDays' | 'sixMonths' | 'year'): void => {
    setSearchFilters(prevValue => ({ ...prevValue, timeRange }));
  };

  const setType = (type: 'attended' | 'all'): void => {
    setSearchFilters(prevValue => ({ ...prevValue, type }));
  };
  const [tableData, setTableData] = useState<AthleteEvent[]>([]);
  const sendAthleteAttendanceMutation = useSendAthleteAttendance();
  const { session } = useAuth();
  const athleteTracking = useAthleteTracking();
  const { successNotification, errorNotification } = notificationHandler();

  const getStartDateRange = (): string => {
    switch (searchFilters.timeRange) {
      case 'thirtyDays':
        return dayjs(today).subtract(30, 'day').format('YYYY-MM-DD');
      case 'ninetyDays':
        return dayjs(today).subtract(90, 'day').format('YYYY-MM-DD');
      case 'sixMonths':
        return dayjs(today).subtract(6, 'month').format('YYYY-MM-DD');
      default:
        return dayjs(today).subtract(1, 'year').format('YYYY-MM-DD');
    }
  };

  const handleSendAthleteAttendance = (): void => {
    sendAthleteAttendanceMutation.mutate(
      {
        startDate: getStartDateRange(),
        athleteEventType: searchFilters.type,
        athleteId: session?.athlete?.athleteId || '',
      },
      {
        onSuccess: () => {
          successNotification(
            'Attendance sent to your email',
            'Information for the period selected has been sent to your email.',
          );

          athleteTracking.trackSendAttendance(getStartDateRange(), searchFilters.type);
        },
        onError: error => {
          errorNotification('Attendance failed to send', error.response?.data.message);
        },
      },
    );
  };
  return (
    <>
      <AttendancePageHeader
        searchFilters={searchFilters}
        setTimeRange={setTimeRange}
        setType={setType}
        showPdfDownloadComponent={handleSendAthleteAttendance}
      />
      <AttendanceTableContainer
        searchFilters={searchFilters}
        setTableData={setTableData}
        tableData={tableData}
        getStartDateRange={getStartDateRange}
      />
    </>
  );
};

export default Attendance;
